'use client';

import { useState } from 'react';
import { Button } from '@repo/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/ui/dialog';
import { PlanSelection } from '@repo/ui/features/groupe/components/plan-selection';
import { useCreateCheckoutSession } from '@repo/ui/hooks/api/payments/use-payments';
import { buildCheckoutRedirectUrls } from '@repo/ui/lib/payments/checkout-redirect-urls';
import { usePublicSubscriptionConfig } from '@repo/ui/hooks/api/settings/use-subscription-public';
import { useGetCurrentUser } from '@repo/ui/hooks/api/users/use-current-user';
import { Loader2, ArrowRight } from 'lucide-react';

interface UpgradePlanDialogProps {
  open: boolean;
  onClose: () => void;
}

export function UpgradePlanDialog({ open, onClose }: UpgradePlanDialogProps) {
  const { data: config } = usePublicSubscriptionConfig();
  const currentUser = useGetCurrentUser();
  const checkout = useCreateCheckoutSession();

  const groupeId = currentUser?.data?.data?.groupe?.id as number | undefined;

  const firstBasicTierId = config?.plans?.BASIC?.tiers?.[0]?.id ?? '';
  const [planType, setPlanType] = useState<'BASIC' | 'ADVANCED'>('BASIC');
  const [tierId, setTierId] = useState<string>(firstBasicTierId);

  function handleSelect(pt: 'BASIC' | 'ADVANCED', t: string): void {
    setPlanType(pt);
    setTierId(t);
  }

  async function handleSubscribe(): Promise<void> {
    if (!groupeId || !tierId) return;

    const { successUrl, cancelUrl } = buildCheckoutRedirectUrls();
    const res = await checkout.mutateAsync({ groupeId, planType, tierId, successUrl, cancelUrl });
    if (res?.url && typeof window !== 'undefined') {
      window.location.href = res.url;
    }
  }

  const isLoading = checkout.isPending;
  const canSubscribe = !!groupeId && !!tierId && !isLoading;
  const isConfigLoaded = !!config?.plans;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-2xl overflow-y-auto max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Choisir un abonnement
          </DialogTitle>
          <DialogDescription>
            Sélectionnez le plan adapté à vos besoins pour débloquer toutes les
            fonctionnalités Dendromap. Votre compte sera mis à jour immédiatement
            après le paiement.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {!isConfigLoaded ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <PlanSelection
              subscriptionConfig={config}
              selectedPlanType={planType}
              selectedTierId={tierId || firstBasicTierId}
              onPlanSelect={handleSelect}
            />
          )}
        </div>

        <DialogFooter className="gap-3 sm:gap-2.5">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="h-10"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubscribe}
            disabled={!canSubscribe}
            className="h-10 gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Redirection...
              </>
            ) : (
              <>
                Souscrire
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
